import React, { ChangeEvent, useMemo, useState } from 'react'
import { TextInputProps } from '../TextInput/types'
import ErrorMessage from '../../ErrorMessage'
import PopOverInfo from '../../PopOverInfo'
import { useController } from 'react-hook-form'
import usePropStyles from '../../utils/usePropStyles'
import { FieldType } from '../../utils/fieldTypeEnum'
import { CountryIso2, defaultCountries, FlagEmoji, parseCountry, usePhoneInput } from 'react-international-phone'

// Mui
import Box from '@mui/material/Box'
import TextField, { TextFieldProps } from '@mui/material/TextField'
import { useTheme } from '@mui/material/styles'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import InputAdornment from '@mui/material/InputAdornment'
import ListSubheader from '@mui/material/ListSubheader'
import SvgIcon from '@mui/material/SvgIcon'
import SearchIcon from '@tdcerhverv/parrotfish/dist/icons/Icons/Search.svg'
import InputMask, { Props as InputmaskProps } from 'react-input-mask'

type PhoneNumber = TextInputProps & {
  mask?: string
  international?: boolean
}

export const PhoneNumber: React.FC<PhoneNumber> = ({
  label,
  mask,
  fieldType,
  fieldWidth,
  assistiveText,
  international = false,
  popover,
  required,
  control,
  disabled,
  ...props
}) => {
  const { palette } = useTheme()
  const styles = useMemo(
    () => ({
      boxStyles: {
        display: 'flex',
        paddingBottom: '24px',
        '& > button': {
          alignSelf: 'baseline',
        },
        '& .MuiSelect-select': {
          minWidth: 0,
        },
        '& img': {
          width: '16px',
        },
      },
      dkNumbers: {
        fontSize: '16px',
      },
      internationalNumbers: {
        fontSize: '16px',
        '& > div': {
          pl: '16px',
        },
      },
      select: {
        width: '100%',
        '& fieldset': {
          display: 'none',
        },
        '& svg': {
          height: '8px',
          right: 0,
        },
        '& .MuiInputBase-input': {
          display: 'flex',
          alignItems: 'center',
          minWidth: '0 !important',
          padding: '0 20px 0 0 !important',
          '& .MuiInputBase-root': {
            display: 'flex',
          },
        },
      },
      flags: {
        width: '16px',
      },
      menuProps: {
        maxWidth: '310px',
        top: '10px',
        left: '-34px',
        '& .MuiMenu-paper': {
          height: '176px',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          width: '100%',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        },
        '& .MuiMenu-list': {
          '.MuiInputBase-adornedStart': {
            padding: 0,
          },
          padding: '8px 0',
          '& li': {
            display: 'flex',
            gap: '10px',
            height: '40px',
            padding: '0 16px',
            '& > *': {
              padding: 0,
              display: 'flex',
              alignItems: 'center',
            },
          },
        },
      },
      firstMenuItem: {
        borderTop: `1px solid ${palette.grey[300]}`,
      },
      searchTextfield: {
        '.MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
        position: 'sticky',
        top: 0,
        zIndex: 1,
        padding: 0,
        background: palette.common.white,
        '& .MuiInputBase-input': {
          color: `${palette.grey[500]}`,
        },
        '& .MuiInputAdornment-root': {
          padding: 0,
          '& > span': {
            padding: 0,
          },
        },
      },
      searchIcon: {
        fontSize: '16px',
        padding: 0,
      },
      placeholder: {
        color: `${palette.grey[500]}`,
      },
    }),
    [],
  )

  const fieldName = props.name!
  const { field, fieldState } = useController({ name: fieldName, control, rules: { required: required! } })
  const hasError = Boolean(fieldState.error)
  const { ref, onChange, ...fieldProps } = field
  const isPhoneInt = fieldType === FieldType.PHONE_INTERNATIONAL
  const [searchValue, setSearchValue] = useState('')
  const propStyles = usePropStyles(fieldWidth)
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    //remove mask from value
    onChange(event.target.value?.replace(/\s+/g, ''))
  }

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const searchText = event.target.value
    setSearchValue(searchText)
  }
  if (isPhoneInt) {
    // International PhoneField
    const { phone, handlePhoneValueChange, inputRef, country, setCountry } = usePhoneInput({
      defaultCountry: 'dk',
      countries: defaultCountries,
      onChange: data => {
        if (data.phone === '+45 ') return
        onChange(data.phone.replace(/\s+/g, ''))
      },
    })

    const filteredCountries = useMemo(() => {
      if (searchValue.trim() === '') {
        return defaultCountries
      } else {
        return defaultCountries.filter(country => country[0].toLowerCase().includes(searchValue.toLowerCase()))
      }
    }, [searchValue])

    return (
      <Box sx={styles.boxStyles}>
        <TextField
          {...props}
          name={fieldName}
          disabled={disabled!}
          label={label}
          value={phone}
          onChange={handlePhoneValueChange}
          inputRef={inputRef}
          sx={[styles.internationalNumbers, propStyles]}
          required={required!}
          error={hasError}
          helperText={
            hasError ? (
              <ErrorMessage>
                <>{fieldState.error?.message}</>
              </ErrorMessage>
            ) : (
              assistiveText
            )
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 0 }}>
                <Select
                  sx={styles.select}
                  value={country}
                  onChange={e => setCountry(e.target.value as CountryIso2)}
                  renderValue={value => <FlagEmoji iso2={value} />}
                  MenuProps={{
                    sx: styles.menuProps,
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    autoFocus: false,
                  }}
                >
                  <ListSubheader>
                    <TextField
                      autoFocus
                      placeholder="Søg land"
                      fullWidth
                      variant="standard"
                      sx={styles.searchTextfield}
                      value={searchValue}
                      onChange={handleSearchInputChange}
                      onKeyDown={e => {
                        if (e.key !== 'Escape') {
                          // Prevents autoselecting item while typing (default Select behaviour)
                          e.stopPropagation()
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SvgIcon component={SearchIcon} viewBox="0 0 48 48" sx={styles.searchIcon} />
                          </InputAdornment>
                        ),
                        disableUnderline: true,
                      }}
                      InputLabelProps={{
                        // Apply the custom style for the placeholder
                        sx: {
                          shrink: styles.placeholder,
                        },
                      }}
                    />
                  </ListSubheader>
                  {filteredCountries.map(c => {
                    const country = parseCountry(c)
                    return (
                      <MenuItem key={country.iso2} value={country.iso2}>
                        <FlagEmoji iso2={country.iso2} style={styles.flags} />
                        <Typography variant="body1">
                          {country.name} (+{country.dialCode})
                        </Typography>
                      </MenuItem>
                    )
                  })}
                </Select>
              </InputAdornment>
            ),
          }}
        />
        {popover && <PopOverInfo {...popover} />}
      </Box>
    )
  }

  return (
    <Box sx={styles.boxStyles}>
      <InputMask {...props} {...fieldProps} onChange={handleChange} mask={mask} maskChar={null}>
        {(inputProps: InputmaskProps & TextFieldProps) => (
          <TextField
            {...props}
            {...inputProps}
            ref={ref}
            sx={[styles.dkNumbers, propStyles]}
            error={hasError}
            label={label}
            helperText={
              hasError ? (
                <ErrorMessage>
                  <>{fieldState.error?.message}</>
                </ErrorMessage>
              ) : (
                assistiveText
              )
            }
            required={required!}
          />
        )}
      </InputMask>
      {popover && <PopOverInfo {...popover} />}
    </Box>
  )
}
